import { Product } from '@interfaces/models/product';
import useLocalStorage from '@hooks/use-local-storage';
import useProductShippingQuery from '@hooks/product/use-product-shipping-query';
import useUser from '@hooks/user/use-user';
import { useMemo } from 'react';

type RecentlyViewedProduct = Product & {
  viewedAt?: number;
};

const useRecentlyViewedProducts = () => {
  const { productShipping } = useProductShippingQuery();
  const { user } = useUser();

  const [recentlyViewedProducts, setRecentlyViewedProducts] = useLocalStorage<RecentlyViewedProduct[]>(
    'localsave/recentlySeenProducts',
    [],
  );

  const addRecentlyViewedProduct = (product: Product) => {
    if (
      productShipping?.buyable &&
      !productShipping?.prohibited &&
      product?.seller?.id !== user?.id &&
      !product?.sold
    ) {
      setRecentlyViewedProducts((prevState: RecentlyViewedProduct[]) => {
        const productId: Product['id'] = product.id;
        const recentlyViewedProductWithTimestamp = { ...product, viewedAt: Math.floor(Date.now() / 1000) };

        // If product already exists in 'recently seen', remove it, and add it back to start, limit max products to 12
        return [
          recentlyViewedProductWithTimestamp,
          ...prevState.filter((recentlyViewedProduct: RecentlyViewedProduct) => {
            return recentlyViewedProduct.id !== productId;
          }),
        ].filter((_, index) => index < 12);
      });
    }
  };

  const recentlyViewedProductsWithCountry = useMemo(
    () =>
      recentlyViewedProducts.map((product) => ({
        isoCountry: product.seller?.countryISO,
        ...product,
      })),
    [recentlyViewedProducts],
  );

  return {
    recentlyViewedProducts: recentlyViewedProductsWithCountry,
    addRecentlyViewedProduct,
  };
};

export default useRecentlyViewedProducts;
